import { FC, useEffect, useMemo, useState } from 'react';
import { DataSourceOverviewCard } from './DataSourceOverviewCard';
import { SearchDropDown } from '@/components/dropdowns';
import { useGetSingleTraitDistributionsQuery } from '@/services/dataSources';
import { Trait } from '@/models/traits';
import { PercentageBadge } from '@/components/badges';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSource } from '@/models/dataSource';
import { DEFAULT_STAT_TRAIT_ID } from '../constant';
import { DataSourceCardLabel } from './DataSourceCardLabel';

type UniqueUsersByTraitCardProps = {
  isStatsFetching: boolean;
  uniqueUsers: number;
  dataSourceId: DataSource['id'];
  userStatTrait?: Trait;
  onTraitChange?: (trait: Trait) => void;
};

export const UniqueUsersByTraitCard: FC<UniqueUsersByTraitCardProps> = ({ uniqueUsers, userStatTrait, isStatsFetching, dataSourceId, onTraitChange }) => {
  const workspace = useWorkspace();
  const [comparisonSearchQuery, setComparisonSearchQuery] = useState<string>('');

  const { isLoading: isTraitsLoading, data: traits } = useGetSingleTraitDistributionsQuery({ workspaceId: workspace.id, datasourceId: dataSourceId });

  useEffect(() => {
    if (traits) {
      onTraitChange(traits.distributions.find((t) => t.trait.id === DEFAULT_STAT_TRAIT_ID)?.trait);
    }
  }, [traits]);

  const selectItems = useMemo(
    () =>
      comparisonSearchQuery?.trim()
        ? traits?.distributions.filter((t) => t.trait.displayName.toLowerCase().includes(comparisonSearchQuery.toLowerCase())).map((t) => t.trait) || []
        : traits?.distributions.map((t) => t.trait) || [],
    [comparisonSearchQuery, traits]
  );

  return (
    <DataSourceOverviewCard title='overview.unique_users' isLoading={isStatsFetching}>
      <div className='flex items-center gap-1'>
        <DataSourceCardLabel label={uniqueUsers} />
        <SearchDropDown
          items={selectItems}
          item={userStatTrait}
          setItem={onTraitChange}
          acceptCustomValue={false}
          setQuery={setComparisonSearchQuery}
          loading={isTraitsLoading}
          readOnly={false}
          openWithFocus={true}
          className={'z-50 w-3/4 cursor-pointer border-0 py-1 text-brand-500'}
        >
          {(item) => {
            const distribution = traits?.distributions.find((d) => d.trait.id === item.id);
            const percentage = distribution && distribution.total ? Math.round((distribution.count / distribution.total) * 100) : 0;
            return (
              <div className='flex'>
                <div className={'whitespace-nowrap'}>{item.displayName}</div>
                <span className={'ml-auto'}>
                  <PercentageBadge percentage={percentage} />
                </span>
              </div>
            );
          }}
        </SearchDropDown>
      </div>
    </DataSourceOverviewCard>
  );
};
